'use client';

import React from 'react';
import Image, { ImageLoaderProps } from 'next/image';
import { useLanguage } from '@/context/LanguageContext';
import { usePortfolioData } from '@/context/PortfolioDataContext';

const passthroughLoader = ({ src }: ImageLoaderProps) => src;

export default function Footer() {
  const { t } = useLanguage();
  const { siteSettings, hero, contact } = usePortfolioData();

  const brandName = siteSettings?.footer_brand_name ?? siteSettings?.site_name ?? '';
  const initials = siteSettings?.footer_initials ?? '';
  const logoUrl = siteSettings?.logo_url ?? null;
  const cvUrl = hero?.cv_url ?? contact?.cv_url ?? '';

  const links = [
    { label: t?.nav_projects, href: '#projects' },
    { label: t?.nav_experience, href: '#experience' },
    { label: t?.nav_contact, href: '#contact' },
    ...(cvUrl ? [{ label: t?.nav_cv, href: cvUrl }] : []),
  ];

  return (
    <footer
      className="relative py-10 px-6"
      style={{ borderTop: '1px solid rgba(200,206,214,0.08)' }}
    >
      <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-5">
        {/* Name / brand */}
        <div className="flex items-center gap-2">
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center font-display font-bold text-xs overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(99,179,237,0.25), rgba(167,139,250,0.25))',
              border: '1px solid rgba(99,179,237,0.3)',
              color: '#EDF2F7',
            }}
          >
            {logoUrl ? (
              <Image
                loader={passthroughLoader}
                unoptimized
                src={logoUrl}
                alt={brandName}
                width={28}
                height={28}
                className="w-full h-full object-contain"
              />
            ) : (
              initials
            )}
          </div>
          <span
            className="font-display font-bold tracking-tight text-sm"
            style={{ color: '#EDF2F7' }}
          >
            {brandName}
          </span>
        </div>

        {/* Links */}
        <nav className="flex items-center gap-7 flex-wrap justify-center">
          {links?.map((link) => (
            <a
              key={link?.label}
              href={link?.href}
              target={link?.href?.startsWith('http') ? '_blank' : undefined}
              rel={link?.href?.startsWith('http') ? 'noopener noreferrer' : undefined}
              className="text-sm font-medium transition-colors duration-200"
              style={{ color: 'rgba(200,206,214,0.55)' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#EDF2F7')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(200,206,214,0.55)')}
            >
              {link?.label}
            </a>
          ))}
        </nav>

        {/* Copyright */}
        <p className="text-xs" style={{ color: 'rgba(200,206,214,0.3)' }}>
          {t?.footer_copyright}
        </p>
      </div>
    </footer>
  );
}
